class FbtRecommendations extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.track = this.querySelector('[data-fbt-track]');
    this.previousButton = this.querySelector('[data-fbt-previous]');
    this.nextButton = this.querySelector('[data-fbt-next]');
    this.dots = this.querySelector('[data-fbt-dots]');
    this.limit = Number(this.dataset.limit || 4);
    this.cardsPerView = 2;
    this.referencePdpLayout = false;
  }

  connectedCallback() {
    this.referencePdpLayout = Boolean(this.closest('.product-section--enhanced-pdp'));
    if (this.referencePdpLayout) {
      this.cardsPerView = 3;
      this.dataset.referencePdpLayout = 'true';
    }

    this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1));
    this.track?.addEventListener('scroll', () => this.scheduleSwipeSync(), { passive: true });
    this.addEventListener('change', (event) => this.updateVariant(event));
    this.addEventListener('click', (event) => this.addRecommendation(event));
    this.resizeObserver = new ResizeObserver(() => this.enforceLayout());
    this.resizeObserver.observe(this);
    this.prepare();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.layoutTimer);
    window.clearTimeout(this.swipeTimer);
  }

  get cards() {
    return [...this.querySelectorAll('[data-fbt-card]')];
  }

  async prepare() {
    this.removeDuplicateAndExcludedCards();

    if (this.cards.length < this.limit && this.dataset.fallbackUrl) {
      await this.loadNativeFallback();
      this.removeDuplicateAndExcludedCards();
    }

    this.cards.slice(this.limit).forEach((card) => card.remove());
    if (this.cards.length === 0) return;

    this.formatReferenceCards();
    this.dataset.ready = 'true';
    this.enforceLayout();
    requestAnimationFrame(() => this.enforceLayout());
    this.layoutTimer = window.setTimeout(() => this.enforceLayout(), 500);
    this.buildDots();
    this.show(0);
  }

  enforceLayout() {
    if (!this.isConnected || !this.track) return;

    const componentWidth = Math.round(this.getBoundingClientRect().width);
    const sideMargin = this.cardsPerView >= 3 ? 12 : (this.cardsPerView > 1 ? 20 : 34);
    const trackWidth = componentWidth - (sideMargin * 2);
    if (trackWidth <= 0) return;

    this.track.style.setProperty('box-sizing', 'border-box', 'important');
    this.track.style.setProperty('margin-left', `${sideMargin}px`, 'important');
    this.track.style.setProperty('margin-right', `${sideMargin}px`, 'important');
    this.track.style.setProperty('width', `${trackWidth}px`, 'important');

    const cardGap = this.cardsPerView >= 3 ? 8 : (this.cardsPerView > 1 ? 10 : 0);
    const cardWidth = (trackWidth - (cardGap * (this.cardsPerView - 1))) / this.cardsPerView;
    const contentWidth = this.cardsPerView > 1 ? cardWidth : Math.min(256, trackWidth);
    this.track.style.setProperty('gap', `${cardGap}px`, 'important');

    this.cards.forEach((card) => {
      card.style.setProperty('box-sizing', 'border-box', 'important');
      card.style.setProperty(
        'display',
        this.referencePdpLayout ? 'flex' : 'block',
        'important',
      );
      if (this.referencePdpLayout) {
        card.style.setProperty('flex-direction', 'column', 'important');
      }
      card.style.setProperty('flex', `0 0 ${cardWidth}px`, 'important');
      card.style.setProperty('min-width', `${cardWidth}px`, 'important');
      card.style.setProperty('width', `${cardWidth}px`, 'important');
      card.querySelectorAll('.fbt-card__summary, .fbt-card__purchase').forEach((section) => {
        section.style.setProperty('box-sizing', 'border-box', 'important');
        section.style.setProperty(
          'margin-left',
          this.referencePdpLayout ? '0' : 'auto',
          'important',
        );
        section.style.setProperty(
          'margin-right',
          this.referencePdpLayout ? '0' : 'auto',
          'important',
        );
        section.style.setProperty('max-width', '100%', 'important');
        section.style.setProperty(
          'width',
          this.referencePdpLayout ? '100%' : `${contentWidth}px`,
          'important',
        );
      });

      if (this.referencePdpLayout) {
        const summary = card.querySelector('.fbt-card__summary');
        const purchase = card.querySelector('.fbt-card__purchase');
        summary?.style.setProperty('flex', '1 1 auto', 'important');
        purchase?.style.setProperty('flex', '0 0 auto', 'important');
        purchase?.style.setProperty('margin-top', 'auto', 'important');
      }
    });
  }

  formatReferenceCards() {
    if (!this.referencePdpLayout) return;

    this.cards.forEach((card) => {
      const button = card.querySelector('[data-fbt-add]');
      const title = card.querySelector('.fbt-card__title')
        ?.textContent
        ?.replace(/\s+/g, ' ')
        .trim();
      if (!button) return;

      button.textContent = this.dataset.addLabel || 'Add';
      button.setAttribute(
        'aria-label',
        button.dataset.fbtAddAriaLabel
          || (title ? `Add ${title} to cart` : 'Add recommendation to cart'),
      );
    });
  }

  excludedIds() {
    return new Set(
      (this.dataset.excludedProductIds || '')
        .split('|')
        .filter(Boolean),
    );
  }

  removeDuplicateAndExcludedCards() {
    const seen = this.excludedIds();
    this.cards.forEach((card) => {
      const productId = card.dataset.productId;
      if (!productId || seen.has(productId)) {
        card.remove();
        return;
      }
      seen.add(productId);
    });
  }

  async loadNativeFallback() {
    try {
      const response = await fetch(this.dataset.fallbackUrl, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fallback = documentFragment.querySelector('[data-fbt-native-fallback]');
      if (!fallback) return;

      [...fallback.querySelectorAll('[data-fbt-card]')]
        .forEach((card) => this.track.append(card));
    } catch (_error) {
      // Stored recommendations remain usable when the optional fallback is unavailable.
    }
  }

  buildDots() {
    if (!this.dots) return;
    const pageCount = Math.ceil(this.cards.length / this.cardsPerView);
    this.dots.innerHTML = Array.from({ length: pageCount })
      .map((_page, index) => `<span class="fbt__dot${index === 0 ? ' is-active' : ''}" data-fbt-dot="${index}"></span>`)
      .join('');
  }

  scheduleSwipeSync() {
    window.clearTimeout(this.swipeTimer);
    this.swipeTimer = window.setTimeout(() => this.syncIndexFromSwipe(), 80);
  }

  syncIndexFromSwipe() {
    if (!this.track || !this.cards.length) return;

    const pageCount = Math.ceil(this.cards.length / this.cardsPerView);
    let nearestPage = 0;
    let nearestDistance = Infinity;

    for (let page = 0; page < pageCount; page += 1) {
      const card = this.cards[page * this.cardsPerView];
      if (!card) continue;

      const targetLeft = Math.max(0, card.offsetLeft - this.track.offsetLeft);
      const distance = Math.abs(this.track.scrollLeft - targetLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestPage = page;
      }
    }

    this.currentIndex = nearestPage;
    this.querySelectorAll('[data-fbt-dot]').forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === this.currentIndex);
    });
  }

  show(index) {
    const cardCount = this.cards.length;
    if (!cardCount) return;

    const pageCount = Math.ceil(cardCount / this.cardsPerView);
    this.currentIndex = pageCount > 1
      ? (index + pageCount) % pageCount
      : 0;
    const targetCard = this.cards[this.currentIndex * this.cardsPerView];
    this.track.scrollTo({
      behavior: 'smooth',
      left: Math.max(0, targetCard.offsetLeft - this.track.offsetLeft),
    });

    this.previousButton.disabled = pageCount <= 1;
    this.nextButton.disabled = pageCount <= 1;
    this.querySelectorAll('[data-fbt-dot]').forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === this.currentIndex);
    });
  }

  updateVariant(event) {
    const selector = event.target.closest('[data-fbt-variant]');
    if (!selector || selector.tagName !== 'SELECT') return;

    const card = selector.closest('[data-fbt-card]');
    const selected = selector.options[selector.selectedIndex];
    const price = card?.querySelector('[data-fbt-price]');
    const image = card?.querySelector('[data-fbt-image]');
    if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
    if (image && selected?.dataset.image) {
      image.src = selected.dataset.image;
      image.removeAttribute('srcset');
    }
  }

  async addRecommendation(event) {
    const button = event.target.closest('[data-fbt-add]');
    if (!button) return;

    const card = button.closest('[data-fbt-card]');
    const variantInput = card?.querySelector('[data-fbt-variant]');
    const status = card?.querySelector('[data-fbt-status]');
    if (!variantInput?.value) return;

    button.disabled = true;
    if (status) status.textContent = this.dataset.addingMessage || 'Adding…';

    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantInput.value), quantity: 1 }],
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || this.dataset.addErrorMessage || 'Unable to add this item.');
      }

      if (status) status.textContent = this.dataset.addedMessage || 'Added to cart';
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: payload },
      }));
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('fbt-recommendations')) {
  customElements.define('fbt-recommendations', FbtRecommendations);
}
